import React from "react";
import PropTypes from "prop-types";
import Button from "./Button";

/**
 * Mi serve questa utility per normalizzare qualsiasi lista incollata
 * (righe, virgole o punti e virgola) in un array pulito pronto per la textarea.
 */
function normalizeListFromText(text) {
	return text
		.split(/[\n,;]+/)
		.map((value) => value.trim())
		.filter(Boolean);
}

/**
 * Ho estratto questa dialog così non sporco la tabella: qui dentro gestisco
 * apertura, normalizzazione del testo e pulsanti di azione in modo isolato.
 */
function AddSecuritiesDialog({ open, onClose, data, universeId, onUpdated, isAdmin }) {
	const [textareaValue, setTextareaValue] = React.useState("");
	const [parsedValues, setParsedValues] = React.useState([]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const textareaId = React.useId();
	const hintId = React.useId();

	const itemsCountLabel = parsedValues.length
		? `${parsedValues.length} elementi`
		: "Nessun elemento";

	const handleTextareaChange = React.useCallback((event) => {
		const rawValue = event.target.value;
		setTextareaValue(rawValue);
		setParsedValues(normalizeListFromText(rawValue));
	}, []);

	const handleCancel = React.useCallback(
		(event) => {
			event.preventDefault();
			onClose();
		},
		[onClose]
	);

	const handleSubmit = React.useCallback(async () => {
		const normalized = parsedValues.join("\n");
		setTextareaValue(normalized);
		setIsSubmitting(true);
		console.log("Submitting updated list from dialog:", parsedValues);

		try {
			const token = localStorage.getItem("token");
			const headers = {
				'Content-Type': 'application/json',
				...(token ? { "Authorization": `Bearer ${token}` } : {})
			};

			const response = await fetch(`/v1/admin/universes/${universeId}/content`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(parsedValues)
			});

			if (response.ok) {
				if (onUpdated) onUpdated();
				onClose();
			} else {
				console.error("Failed to update securities", response.status);
				alert("Errore durante il salvataggio");
			}
		} catch (error) {
			console.error("Error updating securities", error);
			alert("Errore di connessione");
		} finally {
			setIsSubmitting(false);
		}

	}, [onClose, parsedValues, universeId, onUpdated]);

	React.useEffect(() => {
		if (!open) {
			// Reset state when dialog closes
			setTextareaValue("");
			setParsedValues([]);
			return;
		}

		// Populate with existing data when dialog opens
		const defaults = normalizeListFromText(
			(data ?? []).map((item) => item.SecurityID ?? "").join("\n")
		);
		setParsedValues(defaults);
		setTextareaValue(defaults.join("\n"));
	}, [open, data]);

	if (!open) {
		return null;
	}

	return (
		<dialog
			open
			className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4"
			aria-modal="true"
			aria-labelledby="add-securities-title"
			aria-describedby="add-securities-description"
			tabIndex={-1}
			onCancel={handleCancel}
		>
			<div className="w-full max-w-4xl rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
				<div className="border-b border-slate-200 px-5 py-4">
				<h2
					id="add-securities-title"
					className="text-lg font-semibold text-slate-900"
				>
					Edit Securities
				</h2>
				<p
					id="add-securities-description"
					className="mt-1 text-sm text-slate-600"
				>
					Modifica la lista di Security ID (la lista corrente viene sostituita)
				</p>
				</div>

				<div className="space-y-3 px-5 py-4">
					<label
						className="block space-y-2"
						htmlFor={textareaId}
						aria-label="Lista di securities"
					>
						<div className="flex items-center justify-between text-sm text-slate-700">
							<span>Lista di securities</span>
							<span className="text-xs text-slate-500" id={hintId}>
								{itemsCountLabel}
							</span>
						</div>
						<textarea
							id={textareaId}
							aria-describedby={hintId}
							value={textareaValue}
							onChange={handleTextareaChange}
							rows={10}
							className="h-[400px] w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
							placeholder="Incolla ISIN o ticker separati da newline, virgole o ;"
						/>
					</label>
				</div>

				<div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						Close
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Saving..." : "Update List"}
					</Button>
				</div>
			</div>
		</dialog>
	);
}

AddSecuritiesDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	data: PropTypes.array,
	universeId: PropTypes.string,
	onUpdated: PropTypes.func,
	isAdmin: PropTypes.bool,
};

export default AddSecuritiesDialog;

